import { useId, useState } from 'react';

import { Button } from '../Button/Button.tsx';
import { Cell, Column, Row, Table, TableBody, TableHeader } from '../Table/Table.tsx';
import styles from './LeveyJenningsChart.module.css';

export type QcPointOutcome = 'accepted' | 'warning' | 'rejected';

export interface LeveyJenningsPoint {
  id: string;
  /** When the control was measured, in words: `11 Oct 2026 08:05`. */
  label: string;
  /** SDs from the target mean, e.g. 2.5 or -0.75 (from the server's SD index). */
  sdIndex: number;
  /** The measured value in words, with its unit. */
  valueText: string;
  /** "+2.50" */
  sdIndexText: string;
  outcome: QcPointOutcome;
  /** "In control", "Warning", "Rejected". */
  outcomeText: string;
  /** The rules violated, in words, or "". */
  rulesText: string;
}

export interface LeveyJenningsChartProps {
  /** e.g. "Test analyte, Level 1, lot 1234". Names the chart and its table. */
  title: string;
  /** Oldest first. */
  points: readonly LeveyJenningsPoint[];
  /** "Mean 10, SD 1 mmol/L": what the lines are drawn from. */
  targetText: string;
}

/** The plot spans ±4 SD; points beyond are drawn on the edge and say so. */
const LIMIT = 4;
const WIDTH = 640;
const HEIGHT = 220;
const LEFT = 52;
const RIGHT = 12;
const TOP = 10;
const BOTTOM = 10;
const LINES = [3, 2, 1, 0, -1, -2, -3] as const;

const y = (sd: number) =>
  TOP + ((LIMIT - Math.max(-LIMIT, Math.min(LIMIT, sd))) / (2 * LIMIT)) * (HEIGHT - TOP - BOTTOM);

function lineClass(sd: number): string | undefined {
  if (sd === 0) return styles.mean;
  return Math.abs(sd) === 1 ? styles.sd1 : Math.abs(sd) === 2 ? styles.sd2 : styles.sd3;
}

/** A shape per outcome, so the outcome is never told by colour alone. */
function Marker({ x, cy, outcome }: { x: number; cy: number; outcome: QcPointOutcome }) {
  if (outcome === 'rejected') {
    return <rect className={styles.rejected} x={x - 6} y={cy - 6} width={12} height={12} rx={2} />;
  }
  if (outcome === 'warning') {
    return (
      <path
        className={styles.warning}
        d={`M ${String(x)} ${String(cy - 7)} L ${String(x + 7)} ${String(cy)} L ${String(x)} ${String(cy + 7)} L ${String(x - 7)} ${String(cy)} Z`}
      />
    );
  }
  return <circle className={styles.accepted} cx={x} cy={cy} r={5} />;
}

/**
 * A Levey-Jennings chart (lab QC plan §1.1.7): each run of a control plotted as SDs from the
 * lab's target mean, with lines at the mean and ±1, ±2 and ±3 SD. One series, so no legend:
 * the title names it. Rejected runs are hollow squares and warnings diamonds, beside their
 * word in the readout and the table. Each point is focusable and reads its run; "Show as
 * table" gives every run, for screen readers, print and anyone who prefers it. Colour comes
 * from tokens, so every theme and dark mode follow.
 */
export function LeveyJenningsChart({ title, points, targetText }: LeveyJenningsChartProps) {
  const [asTable, setAsTable] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const titleId = useId();
  const shown = active ?? points.length - 1;
  const readout = points[shown];
  const step = points.length <= 1 ? 0 : (WIDTH - LEFT - RIGHT - 16) / (points.length - 1);
  const xOf = (i: number) =>
    LEFT + 8 + (points.length <= 1 ? (WIDTH - LEFT - RIGHT) / 2 : i * step);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i).toFixed(1)} ${y(p.sdIndex).toFixed(1)}`)
    .join(' ');
  return (
    <figure className={styles.figure} aria-labelledby={titleId}>
      <figcaption className={styles.caption}>
        <span>
          <span id={titleId} className={styles.title}>
            {title}
          </span>
          <span className={styles.target}>{targetText}</span>
        </span>
        <Button
          dense
          variant="quiet"
          onPress={() => {
            setAsTable((v) => !v);
          }}
        >
          {asTable ? 'Show as chart' : 'Show as table'}
        </Button>
      </figcaption>
      {asTable ? (
        <Table flat aria-label={title}>
          <TableHeader>
            <Column isRowHeader>Measured</Column>
            <Column numeric>Value</Column>
            <Column numeric>SD index</Column>
            <Column>Outcome</Column>
            <Column>Rules</Column>
          </TableHeader>
          <TableBody items={points.map((p) => ({ ...p }))}>
            {(p) => (
              <Row id={p.id}>
                <Cell>{p.label}</Cell>
                <Cell numeric>{p.valueText}</Cell>
                <Cell numeric>{p.sdIndexText}</Cell>
                <Cell>{p.outcomeText}</Cell>
                <Cell>{p.rulesText}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : (
        <>
          <p className={styles.readout} aria-live="polite">
            {readout === undefined
              ? 'No runs yet.'
              : `${readout.label}: ${readout.valueText}, ${readout.sdIndexText} SD, ${readout.outcomeText}${readout.rulesText === '' ? '' : ` (${readout.rulesText})`}`}
          </p>
          <svg
            className={styles.plot}
            viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
            role="group"
            aria-label={title}
          >
            {LINES.map((sd) => (
              <g key={sd} aria-hidden="true">
                <line
                  className={lineClass(sd)}
                  x1={LEFT}
                  x2={WIDTH - RIGHT}
                  y1={y(sd)}
                  y2={y(sd)}
                />
                <text className={styles.axis} x={LEFT - 6} y={y(sd) + 4} textAnchor="end">
                  {sd === 0 ? 'Mean' : `${sd > 0 ? '+' : ''}${String(sd)} SD`}
                </text>
              </g>
            ))}
            {points.length > 1 ? (
              <path className={styles.line} d={path} aria-hidden="true" />
            ) : null}
            {points.map((p, i) => {
              const beyond = Math.abs(p.sdIndex) > LIMIT;
              return (
                <g
                  key={p.id}
                  className={styles.point}
                  tabIndex={0}
                  role="img"
                  aria-label={`${p.label}: ${p.valueText}, ${p.sdIndexText} SD, ${p.outcomeText}${beyond ? ', beyond the chart' : ''}`}
                  data-active={i === shown}
                  onPointerEnter={() => {
                    setActive(i);
                  }}
                  onPointerLeave={() => {
                    setActive(null);
                  }}
                  onFocus={() => {
                    setActive(i);
                  }}
                  onBlur={() => {
                    setActive(null);
                  }}
                >
                  {/* A hit target larger than the mark. */}
                  <rect
                    className={styles.hit}
                    x={xOf(i) - 10}
                    y={TOP}
                    width={20}
                    height={HEIGHT - TOP - BOTTOM}
                  />
                  <Marker x={xOf(i)} cy={y(p.sdIndex)} outcome={p.outcome} />
                </g>
              );
            })}
          </svg>
        </>
      )}
    </figure>
  );
}
